import React from "react";
import { Button } from "components/DesignSystem/Button/Button";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { StateRegistrationSuggestion } from "store/apis/StateRegistrations";
import { SmallInfo } from "components/icons/SmallInfo";

type SuggestionsBannerProps = {
  suggestions: StateRegistrationSuggestion[];
  onStartRegistration: (stateId: string) => void;
};

const SUGGESTION_TYPE_LABEL: Record<
  StateRegistrationSuggestion["suggestion_type"],
  string
> = {
  in_cart: "payroll",
  foreign_qualification_in_cart: "foreign qualification",
};

export const SuggestionsBanner = ({
  suggestions,
  onStartRegistration,
}: SuggestionsBannerProps) => {
  const [dismissedStateIds, setDismissedStateIds] = React.useState<Set<string>>(
    new Set()
  );
  const [showPicker, setShowPicker] = React.useState(false);
  const [selectedStateId, setSelectedStateId] = React.useState<string | null>(
    null
  );

  const pendingCartRegistrations = suggestions.filter(
    (s) =>
      (s.suggestion_type === "in_cart" ||
        s.suggestion_type === "foreign_qualification_in_cart") &&
      !dismissedStateIds.has(s.state_id)
  );

  const getPendingRegistrationsBannerDescription = () => {
    if (pendingCartRegistrations.length === 1) {
      const suggestion = pendingCartRegistrations[0];
      const typeLabel = SUGGESTION_TYPE_LABEL[suggestion.suggestion_type];
      return `You have 1 registration in your cart — ${suggestion.state} ${typeLabel}. Start it with Inkle today.`;
    }
    return `You have ${pendingCartRegistrations.length} registrations in your cart. Start them with Inkle today.`;
  };

  const dismiss = () =>
    setDismissedStateIds(
      (prev) =>
        new Set([...prev, ...pendingCartRegistrations.map((s) => s.state_id)])
    );

  const handleCtaClick = () => {
    if (pendingCartRegistrations.length === 1) {
      onStartRegistration(pendingCartRegistrations[0].state_id);
      return;
    }
    setShowPicker(true);
  };

  const handlePickerOpenChange = (open: boolean) => {
    setShowPicker(open);
    if (!open) setSelectedStateId(null);
  };

  const handlePickerConfirm = () => {
    if (!selectedStateId) return;
    onStartRegistration(selectedStateId);
    setShowPicker(false);
    setSelectedStateId(null);
  };

  const selectedOption = pendingCartRegistrations.find(
    (s) => s.state_id === selectedStateId
  );

  if (pendingCartRegistrations.length === 0) {
    return null;
  }

  return (
    <>
      <Banner
        type="info"
        icon={<SmallInfo color="currentColor" />}
        description={getPendingRegistrationsBannerDescription()}
        onClose={dismiss}
        cta={
          <Button size="small" customType="secondary" onClick={handleCtaClick}>
            Start registration
          </Button>
        }
      />
      <Modal.Root open={showPicker} onOpenChange={handlePickerOpenChange}>
        <Modal.Content useCustomOverlay>
          <Modal.Header>
            <Modal.Title>Select a state to register first</Modal.Title>
          </Modal.Header>
          <div className="t-px-6 t-py-4 t-flex t-flex-col t-gap-4">
            <p className="t-m-0 t-text-body-sm t-text-text-30">
              You have multiple registrations in your cart. Which one would you
              like to start with Inkle first?
            </p>
            <Combobox
              placeholder="Select a state"
              options={pendingCartRegistrations.map((s) => ({
                label: s.state,
                value: s.state_id,
              }))}
              value={
                selectedOption
                  ? {
                      label: selectedOption.state,
                      value: selectedOption.state_id,
                    }
                  : null
              }
              onChange={(option) =>
                setSelectedStateId(
                  (option as { value: string } | null)?.value ?? null
                )
              }
            />
          </div>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button type="button" customType="secondary">
                Cancel
              </Button>
            </Modal.RawClose>
            <Button
              type="button"
              customType="primary"
              disabled={!selectedStateId}
              onClick={handlePickerConfirm}
            >
              Start registration
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
